import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, Search, X, Check, FileText, Shield, Wallet
} from 'lucide-react';
import {
  FaHandshake,
  FaTruck,
  FaChartLine,
  FaShieldAlt,
  FaFileInvoiceDollar,
  FaBuilding,
  FaUserShield
} from 'react-icons/fa';
import HandRupeeIcon from '../icons/HandRupeeIcon';
import PurchaseCartIcon from '../icons/PurchaseCartIcon';
import ChannelFinanceIcon from '../icons/ChannelFinanceIcon';
import GlobeImportIcon from '../icons/GlobeImportIcon';
import GlobeExportIcon from '../icons/GlobeExportIcon';
import NavigationExact from '../components/NavigationExact';
import FooterExact from '../components/FooterExact';

const ServicesPage = () => {
  // Page component
  const [activeTab, setActiveTab] = useState('domestic');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  // Auto-scroll horizontal carousel
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let scrollAmount = 0;
    const scrollSpeed = 1.5;
    const maxScroll = container.scrollWidth - container.clientWidth;
    let animationId;

    const autoScroll = () => {
      scrollAmount += scrollSpeed;

      if (scrollAmount >= maxScroll) {
        scrollAmount = 0;
      }

      container.scrollLeft = scrollAmount;
      animationId = requestAnimationFrame(autoScroll);
    };

    animationId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationId);
  }, [activeTab]);

  const domesticProducts = [
    {
      icon: FaHandshake,
      title: 'Vendor Finance',
      desc: 'Pay your suppliers early without tying up your own cash. Funding Sathi connects you with capital partners who settle your vendor invoices — keeping your supply chain moving.',
      fullDescription: 'Vendor Finance enables you to pay suppliers early without straining your cash flow. We connect you with capital partners who settle your invoices, maintaining smooth supply chain operations.',
      features: ['Early supplier payments', 'Preserved cash flow', 'Stronger vendor relationships', 'Quick onboarding', 'Real-time tracking'],
      link: '#'
    },
    {
      icon: FaTruck,
      title: 'Dealer Finance',
      desc: 'Help your dealers stock more inventory without waiting for their own credit lines. Funding Sathi orchestrates inventory financing so your dealers sell more and you grow faster.',
      fullDescription: 'Dealer Finance helps your dealership network stock more inventory and sell more without waiting for their own credit approvals. We structure financing around your sales cycle.',
      features: ['Inventory financing', 'Stocking loans', 'Seasonal credit lines', 'No dealer credit checks', 'Multi-brand coverage'],
      link: '#'
    },
    {
      icon: HandRupeeIcon,
      title: 'Working Capital Loan',
      desc: 'Access flexible, on-demand capital for day-to-day operations without pledging fixed assets. Bridge payroll, raw materials, or seasonal gaps — you only pay for what you use.',
      fullDescription: 'Working Capital Loans provide flexible, on-demand capital for daily operations without requiring fixed asset collateral. Bridge gaps in payroll, raw materials, or seasonal fluctuations.',
      features: ['No fixed asset pledge', 'Flexible drawdowns', 'Pay for what you use', 'Quick approval', 'Daily operations support'],
      link: '#'
    },
    {
      icon: ChannelFinanceIcon,
      title: 'Channel Finance',
      desc: 'Unlock liquidity at every tier of your distribution network — from distributors to sub-dealers to retailers. Funding Sathi structures multi-tier financing programs built around your sales cycle.',
      fullDescription: 'Channel Finance unlocks liquidity across your entire distribution network. We structure multi-tier financing from distributors to sub-dealers to retailers, aligned with your sales cycle.',
      features: ['Multi-tier coverage', 'Distributor financing', 'Sub-dealer support', 'Retailer programs', 'Sales cycle alignment'],
      link: '#'
    },
    {
      icon: FaChartLine,
      title: 'Private Credit',
      desc: 'Access institutional-scale capital for strategic growth, expansion, or special projects. Funding Sathi connects mid-to-large enterprises with private credit funds and custom repayment terms.',
      fullDescription: 'Private Credit provides institutional-scale capital for strategic initiatives. We connect mid-to-large enterprises with private credit funds offering customized repayment structures.',
      features: ['Institutional-scale capital', 'Strategic growth funding', 'Custom repayment terms', 'Expansion financing', 'Private credit fund access'],
      link: '#'
    },
    {
      icon: FaShieldAlt,
      title: 'Special Situation Funding',
      desc: 'Facing a gap that banks won\'t fill? Bridge funding, urgent liquidity, or stressed situations — we structure solutions traditional lenders can\'t touch.',
      fullDescription: 'Special Situation Funding addresses gaps that traditional banks cannot fill. We provide bridge funding, urgent liquidity, and solutions for stressed situations with flexible structures.',
      features: ['Bridge funding', 'Urgent liquidity', 'Stressed situation support', 'Non-bank solutions', 'Flexible structuring'],
      link: '#'
    },
    {
      icon: FaFileInvoiceDollar,
      title: 'Invoice Discounting',
      desc: 'Convert your unpaid invoices into immediate cash without waiting for customer payment cycles. Funding Sathi connects you with capital partners who settle your invoices within 48 hours.',
      fullDescription: 'Invoice Discounting converts your unpaid invoices into immediate cash. We connect you with capital partners who settle invoices within 48 hours, eliminating payment cycle delays.',
      features: ['Immediate cash conversion', '48-hour settlement', 'No waiting for customers', 'Verified invoice backing', 'Working capital boost'],
      link: '#'
    },
    {
      icon: PurchaseCartIcon,
      title: 'Purchase Finance',
      desc: 'Finance your procurement seamlessly while extending your payment cycle. Funding Sathi connects businesses with capital partners who fund purchases so you can pay after your sales cycle completes.',
      fullDescription: 'Purchase Finance enables seamless procurement financing with extended payment cycles. We connect you with capital partners who fund purchases, allowing payment after your sales cycle.',
      features: ['Procurement financing', 'Extended payment terms', 'Sales cycle alignment', 'Seamless funding', 'Working capital preservation'],
      link: '#'
    }
  ];

  const internationalProducts = [
    {
      icon: FaBuilding,
      title: 'Acquisition Finance',
      desc: 'Fund strategic acquisitions without diluting equity or exhausting bank lines. Funding Sathi connects acquirers with institutional lenders and private credit funds for M&A transactions.',
      fullDescription: 'Acquisition Finance helps you fund strategic acquisitions without diluting equity or exhausting bank lines. We connect acquirers with institutional lenders and private credit funds for M&A transactions, ensuring you have the capital needed for growth.',
      features: ['Non-dilutive funding', 'M&A transaction support', 'Institutional lender network', 'Private credit fund access', 'Flexible repayment terms'],
      link: '#'
    },
    {
      icon: GlobeImportIcon,
      title: 'Import Finance',
      desc: 'Fund your global procurement with confidence and competitive terms. Funding Sathi connects Indian importers with capital partners who provide import financing and trade credit.',
      fullDescription: 'Import Finance allows you to fund your global procurement with confidence and competitive terms. We connect Indian importers with capital partners who provide import financing and trade credit solutions.',
      features: ['Letter of Credit issuance', 'Usance bill financing', 'Import loans', 'Currency risk management', 'Competitive forex rates'],
      link: '#'
    },
    {
      icon: GlobeExportIcon,
      title: 'Export Finance',
      desc: 'Grow your global sales without worrying about payment delays or buyer credit risk. Funding Sathi connects Indian exporters with capital partners who provide export financing solutions.',
      fullDescription: 'Export Finance helps you grow your global sales without worrying about payment delays or buyer credit risk. We connect Indian exporters with capital partners who provide comprehensive export financing solutions.',
      features: ['Pre-shipment financing', 'Post-shipment financing', 'Export factoring', 'Credit insurance', 'Buyer credit risk assessment'],
      link: '#'
    },
    {
      icon: FaUserShield,
      title: 'Buyer Credit',
      desc: 'Secure extended payment terms for large domestic and international procurement — without straining supplier relationships or your cash flow.',
      fullDescription: 'Buyer Credit allows you to secure extended payment terms for large domestic and international procurement without straining supplier relationships or your cash flow. Get the flexibility you need to grow.',
      features: ['Extended payment terms', 'Supplier relationship protection', 'Cash flow optimization', 'No collateral required', 'Quick approval process'],
      link: '#'
    }
  ];

  // Handle query param to auto-open product
  useEffect(() => {
    const productQuery = searchParams.get('product');
    if (productQuery) {
      const decodedProduct = decodeURIComponent(productQuery);
      // Combine all products for lookup
      const allProducts = [...domesticProducts, ...internationalProducts];
      const foundProduct = allProducts.find(p => 
        p.title.toLowerCase().includes(decodedProduct.toLowerCase()) ||
        decodedProduct.toLowerCase().includes(p.title.toLowerCase().split(' ')[0])
      );
      if (foundProduct) {
        setSelectedProduct(foundProduct);
        // Set active tab based on product
        const isInternational = internationalProducts.some(p => p.title === foundProduct.title);
        if (isInternational) {
          setActiveTab('international');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <NavigationExact />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-50 via-white to-red-50/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-red-100 to-red-50 text-[#5C1F33] rounded-full text-sm font-semibold mb-8 shadow-sm"
          >
            <span className="w-2 h-2 bg-[#5C1F33] rounded-full animate-pulse"></span>
            Financial Solutions
          </motion.span>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-8 leading-[1.1] tracking-tight"
          >
            Our <span className="text-[#5C1F33]">Services</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-light"
          >
            From invoice discounting to dealer finance — five structured solutions built around how Indian trade actually moves
          </motion.p>

          <motion.button
            type="button"
            onClick={() => navigate('/contact')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 mt-10 bg-[#5C1F33] text-white px-8 py-4 rounded-xl hover:bg-[#800020] hover:shadow-xl hover:shadow-[#5C1F33]/20 transition-all duration-300 font-medium text-lg"
          >
            Get Started
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.button>
        </div>
      </section>

      {/* Our Products Section with Tabs */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[#5C1F33] font-semibold text-sm uppercase tracking-wider mb-4 block"
            >
              What We Offer
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight"
            >
              Our <span className="text-[#5C1F33]">Products</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Comprehensive financial solutions tailored to meet the diverse needs of businesses across India and globally
            </motion.p>

            {/* Tab Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap justify-center bg-gray-100 rounded-full p-1.5 mb-8 gap-1"
            >
              <button
                onClick={() => setActiveTab('domestic')}
                className={`px-6 sm:px-8 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'domestic'
                    ? 'bg-[#5C1F33] text-white shadow-lg shadow-[#5C1F33]/25'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Domestic Solutions
              </button>
              <button
                onClick={() => setActiveTab('international')}
                className={`px-6 sm:px-8 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'international'
                    ? 'bg-[#5C1F33] text-white shadow-lg shadow-[#5C1F33]/25'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                International Solutions
              </button>
            </motion.div>

            {/* Tab Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-gray-500 text-lg max-w-2xl mx-auto"
            >
              {activeTab === 'domestic' 
                ? 'India-focused financing products designed for local supply chains and business operations'
                : 'Global financing solutions for cross-border trade and international operations'
              }
            </motion.p>
          </div>

          {/* Products Grid - Horizontal scrollable cards */}
          <AnimatePresence mode="wait">
            <motion.div 
              ref={scrollRef}
              key={activeTab}
              className="flex gap-8 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide cursor-grab active:cursor-grabbing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {(activeTab === 'domestic' ? domesticProducts : internationalProducts).map((product, index) => {
                const isSelected = selectedProduct?.title === product.title;
                return (
                  <motion.div
                    key={product.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                    className={`group bg-white rounded-3xl p-8 sm:p-10 border transition-all duration-300 h-full cursor-pointer flex-shrink-0 w-[340px] sm:w-[400px] snap-start ${
                      product.featured 
                        ? 'border-[#5C1F33] shadow-2xl shadow-[#5C1F33]/15' 
                        : 'border-gray-100 hover:border-[#5C1F33]/30 hover:shadow-2xl hover:shadow-[#5C1F33]/10'
                    }`}
                  >
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 transition-all duration-300 border border-[#5C1F33]/20 ${isSelected ? 'bg-[#5C1F33] ring-2 ring-white ring-offset-2 ring-offset-[#5C1F33]/10' : 'bg-[#5C1F33]/10 group-hover:bg-[#5C1F33] group-hover:scale-110'}`}>
                      <product.icon
                        className={`w-8 h-8 transition-colors duration-300 ${isSelected ? 'text-white' : 'text-[#4A4A4A] group-hover:text-white'}`}
                        style={isSelected ? { color: '#ffffff', fill: '#ffffff', stroke: '#ffffff' } : undefined}
                      />
                    </div>
                    
                    <h3 className={`text-2xl font-bold mb-5 ${
                      product.featured ? 'text-[#5C1F33]' : 'text-gray-900 group-hover:text-[#5C1F33]'
                    } transition-colors`}>{product.title}</h3>
                    <p className="text-gray-600 text-base leading-[1.8] mb-8">
                      {product.desc}
                    </p>
                    
                    <button 
                      onClick={() => setSelectedProduct(product)}
                      className="inline-flex items-center gap-2 text-base font-semibold text-[#5C1F33] hover:gap-3 transition-all group/link"
                    >
                      Learn More 
                      <ArrowRight className="w-5 h-5 transition-transform group-hover/link:translate-x-1" />
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Custom Solution CTA */}
      <section className="py-24 md:py-32 bg-[#5C1F33]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight"
          >
            Need a Custom Solution?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Our team of financial experts can create a tailored financing package that perfectly fits your business needs
          </motion.p>
          <motion.button
            type="button"
            onClick={() => navigate('/contact')}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-[#5C1F33] px-8 py-4 rounded-xl hover:bg-[#5C1F33] hover:text-white hover:shadow-xl hover:shadow-[#5C1F33]/30 transition-all duration-300 font-medium text-lg"
          >
            Get in Touch
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.button>
        </div>
      </section>

      {/* Process Section */}
      <section id="how-it-works" className="py-24 md:py-32 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden">
        {/* Background decorative line */}
        <div className="absolute top-[45%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5C1F33]/10 to-transparent hidden md:block" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[#5C1F33] font-semibold text-sm uppercase tracking-[0.2em] mb-4 block"
            >
              The Process
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-gray-800"
            >
              How It <span className="text-[#5C1F33]">Works</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto"
            >
              Simple steps to get your business funded
            </motion.p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
            {[
              { step: '01', icon: FileText, title: 'Submit Your Invoice', desc: 'Upload your GST-verified invoice and basic business details — takes less than 5 minutes' },
              { step: '02', icon: Search, title: 'Trade Verification', desc: 'Our AI engine verifies your invoice against GSTN and e-invoice records instantly' },
              { step: '03', icon: Shield, title: 'Get Funded in 48 Hours', desc: 'Receive approval with clear terms — most invoices are approved within 48–72 hours' },
              { step: '04', icon: Wallet, title: 'Money in Your Account', desc: 'Funds transferred directly to your bank account — no middlemen, no delays' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative text-center group"
              >
                {/* Connector line for desktop */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-full h-[2px] bg-gradient-to-r from-[#5C1F33]/30 to-transparent -z-10" />
                )}

                {/* Icon container with step number */}
                <div className="relative inline-block mb-8">
                  {/* Step number badge - positioned top right */}
                  <div className="absolute -top-2 -right-2 w-9 h-9 bg-[#5C1F33] text-white text-sm font-bold rounded-full flex items-center justify-center z-20 shadow-lg shadow-[#5C1F33]/30 border-2 border-white">
                    {item.step}
                  </div>
                  
                  {/* Icon box with pinkish background */}
                  <div className="w-24 h-24 rounded-[1.5rem] bg-gradient-to-br from-red-50 to-red-100/80 border border-[#5C1F33]/10 flex items-center justify-center mx-auto group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-[#5C1F33]/10 transition-all duration-500">
                    <item.icon className="w-10 h-10 text-[#5C1F33]" strokeWidth={1.5} />
                  </div>
                </div>
                
                {/* Title */}
                <h4 className="font-bold text-xl md:text-2xl mb-4 text-gray-900 group-hover:text-[#5C1F33] transition-colors duration-300">
                  {item.title}
                </h4>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-base max-w-[250px] mx-auto">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      </div>

      {/* Product Detail Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProduct(null)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-[0_25px_50px_rgba(0,0,0,0.25)] border border-gray-100"
            >
              {/* Modal Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-2xl flex items-center justify-center flex-shrink-0">
                  {selectedProduct.icon && (
                    <selectedProduct.icon className="w-8 h-8 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{selectedProduct.title}</h3>
                  {selectedProduct.featured && (
                    <span className="inline-block px-3 py-1 bg-[#5C1F33]/10 text-[#5C1F33] rounded-full text-sm font-medium">
                      Featured Service
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors relative z-10"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Full Description */}
              <div className="mb-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  {selectedProduct.fullDescription || selectedProduct.desc}
                </p>
              </div>

              {/* Key Features */}
              {selectedProduct.features && (
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                    Key Features
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {selectedProduct.features.map((feature, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3"
                      >
                        <div className="w-8 h-8 bg-[#5C1F33]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-[#5C1F33]" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:info@fundingsathi.in"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#5C1F33] to-[#800020] text-white px-6 py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5C1F33]/25 transition-all duration-300"
                >
                  Apply Now
                  <ArrowRight className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-700 px-6 py-3.5 rounded-xl font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FooterExact />
    </>
  );
};

export default ServicesPage;
